import React, { Fragment, useState } from "react";

import { roundToWhole, useTranslation } from "utils";

import { Icon } from "components";

import { EveryLineDifference, useEveryLineTotal } from "pages/app/scenario-analysis/components/financial-breakdown/every-line/EveryLineDifference";
import { LineMarker } from "pages/app/scenario-analysis/components/financial-breakdown/every-line/LineMarker";
import type { CmCategoryRow, CmLineBreakdown, CmLineRow, CmSubCategoryRow } from "pages/app/scenario-analysis/utils/cmLines";
import { formatSignedPercent } from "pages/app/scenario-analysis/utils/comparisonFormatting";

type EveryLineTotalsTableProps = {
  breakdown: CmLineBreakdown;
  nameA: string;
  nameB: string;
  marketPricePct: number;
  costDevelopmentPct: number;
  dataTestId: string;
};

type RowProps = {
  rowKey: string;
  depth: number;
  label: React.ReactNode;
  totalA?: number;
  totalB?: number;
  /** As the breakdown worked it out (the rounded a minus the rounded b), so the row and its label agree. */
  difference?: number;
  canOpen: boolean;
  isStrong?: boolean;
  /** A folded "same in both" row: it reads "no difference" instead of figures. */
  isQuiet?: boolean;
};

/** N-year totals per line, grouped as category → sub-category → line, with unchanged rows folded away. */
export const EveryLineTotalsTable = ({ breakdown, nameA, nameB, marketPricePct, costDevelopmentPct, dataTestId }: EveryLineTotalsTableProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const formatTotal = useEveryLineTotal();
  const [openRows, setOpenRows] = useState<Set<string>>(() => new Set(["revenue", "costs"]));

  const toggle = (rowKey: string) =>
    setOpenRows((previous) => {
      const next = new Set(previous);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });

  const renderRow = ({ rowKey, depth, label, totalA = 0, totalB = 0, difference = 0, canOpen, isStrong = false, isQuiet = false }: RowProps) => {
    const isOpen = openRows.has(rowKey);
    return (
      <div
        key={rowKey}
        role="row"
        className={`every-line-row every-line-row--depth-${depth}${isStrong ? " every-line-row--strong" : ""}${canOpen ? " every-line-row--openable" : ""}`}
        data-testid={`${dataTestId}-row-${rowKey}`}
      >
        <span role="rowheader" className="every-line-cell every-line-label-cell">
          {canOpen ? (
            <button
              type="button"
              className="every-line-toggle"
              onClick={() => toggle(rowKey)}
              aria-expanded={isOpen}
              data-testid={`${dataTestId}-toggle-${rowKey}`}
            >
              {label}
            </button>
          ) : (
            label
          )}
        </span>
        {isQuiet ? (
          <span role="cell" className="every-line-cell every-line-cell--quiet">
            {t("everyLine.noDifference")}
          </span>
        ) : (
          <>
            <span role="cell" className="every-line-cell every-line-cell--value every-line-cell--muted">
              {formatTotal(totalB)}
            </span>
            <span role="cell" className="every-line-cell every-line-cell--value every-line-cell--muted">
              {formatTotal(totalA)}
            </span>
            <span role="cell" className="every-line-cell every-line-cell--value">
              <EveryLineDifference value={difference} />
            </span>
          </>
        )}
        <span aria-hidden className="every-line-cell every-line-cell--chevron">
          {canOpen ? <Icon iconName="tabler/chevron-right" size="s" className={isOpen ? "every-line-chevron-open" : undefined} /> : null}
        </span>
      </div>
    );
  };

  const markerText = (line: CmLineRow) => {
    if (line.definition.marker === "model") return t("everyLine.key.model");
    if (line.definition.marker === "trend") return t("everyLine.key.trend");
    return undefined;
  };

  const lineLabel = (line: CmLineRow) => {
    const { definition } = line;
    const rate = definition.isTrendOf === "price" ? marketPricePct : costDevelopmentPct;
    return (
      <span className="every-line-label">
        <LineMarker marker={definition.marker} tone={roundToWhole(line.difference) >= 0 ? "positive" : "negative"} screenReaderText={markerText(line)} />
        {definition.isTrendOf ? t(`everyLine.lines.${line.labelKey}`, { rate: formatSignedPercent(rate) }) : t(`everyLine.lines.${line.labelKey}`)}
      </span>
    );
  };

  const lineRow = (line: CmLineRow, depth: number) =>
    renderRow({
      rowKey: line.definition.id,
      depth,
      canOpen: false,
      label: lineLabel(line),
      totalA: line.totalA,
      totalB: line.totalB,
      difference: line.difference,
    });

  const subCategoryRows = (category: CmCategoryRow, subCategory: CmSubCategoryRow) => {
    const rowKey = `${category.category}-${subCategory.subCategory}`;
    const sameKey = `${rowKey}-same`;
    const moving = subCategory.lines.filter((line) => !line.isSameInBoth);
    const unchanged = subCategory.lines.filter((line) => line.isSameInBoth);
    return (
      <Fragment key={rowKey}>
        {renderRow({
          rowKey,
          depth: 1,
          canOpen: true,
          label: t(`everyLine.subCategories.${subCategory.subCategory}`),
          totalA: subCategory.totalA,
          totalB: subCategory.totalB,
          difference: subCategory.difference,
        })}
        {openRows.has(rowKey) ? (
          <>
            {moving.map((line) => lineRow(line, 2))}
            {unchanged.length && moving.length ? (
              <>
                {renderRow({ rowKey: sameKey, depth: 2, canOpen: true, isQuiet: true, label: t("everyLine.sameLines", { count: unchanged.length }) })}
                {openRows.has(sameKey) ? unchanged.map((line) => lineRow(line, 3)) : null}
              </>
            ) : (
              unchanged.map((line) => lineRow(line, 2))
            )}
          </>
        ) : null}
      </Fragment>
    );
  };

  const categoryBlock = (category: CmCategoryRow) => {
    const sameKey = `${category.category}-same`;
    const moving = category.subCategories.filter((subCategory) => !subCategory.isSameInBoth);
    const unchanged = category.subCategories.filter((subCategory) => subCategory.isSameInBoth);
    return (
      <div key={category.category} role="rowgroup" className="every-line-block">
        {renderRow({
          rowKey: category.category,
          depth: 0,
          canOpen: true,
          isStrong: true,
          label: t(`everyLine.categories.${category.category}`),
          totalA: category.totalA,
          totalB: category.totalB,
          difference: category.difference,
        })}
        {openRows.has(category.category) ? (
          <>
            {moving.map((subCategory) => subCategoryRows(category, subCategory))}
            {unchanged.length ? (
              <>
                {renderRow({
                  rowKey: sameKey,
                  depth: 1,
                  canOpen: true,
                  isQuiet: true,
                  label: t("everyLine.sameSubCategories", {
                    names: unchanged.map((subCategory) => t(`everyLine.subCategories.${subCategory.subCategory}`)).join(", "),
                  }),
                })}
                {openRows.has(sameKey) ? unchanged.map((subCategory) => subCategoryRows(category, subCategory)) : null}
              </>
            ) : null}
          </>
        ) : null}
      </div>
    );
  };

  return (
    <div className="every-line-scroll">
      <div role="table" aria-label={t("everyLine.title")} className="every-line-table" data-testid={`${dataTestId}-totals`}>
        <div role="row" className="every-line-row every-line-row--header">
          <span role="columnheader" className="every-line-cell every-line-label-cell">
            {t("everyLine.header.horizon", { count: breakdown.horizonYears })}
          </span>
          <span role="columnheader" className="every-line-cell every-line-cell--value every-line-cell--name" title={nameB}>
            {nameB}
          </span>
          <span role="columnheader" className="every-line-cell every-line-cell--value every-line-cell--name" title={nameA}>
            {nameA}
          </span>
          <span role="columnheader" className="every-line-cell every-line-cell--value">
            {t("everyLine.header.difference")}
          </span>
          <span aria-hidden className="every-line-cell every-line-cell--chevron" />
        </div>
        {breakdown.categories.map(categoryBlock)}
        <div role="row" className="every-line-row every-line-row--cm" data-testid={`${dataTestId}-row-cm`}>
          <span role="rowheader" className="every-line-cell every-line-label-cell">
            {t("everyLine.contributionMargin")}
          </span>
          <span role="cell" className="every-line-cell every-line-cell--value">
            {formatTotal(breakdown.cm.totalB)}
          </span>
          <span role="cell" className="every-line-cell every-line-cell--value">
            {formatTotal(breakdown.cm.totalA)}
          </span>
          <span role="cell" className="every-line-cell every-line-cell--value">
            <EveryLineDifference value={breakdown.cm.difference} />
          </span>
          <span aria-hidden className="every-line-cell every-line-cell--chevron" />
        </div>
      </div>
    </div>
  );
};

export default EveryLineTotalsTable;
